import { useCallback, useMemo } from 'react'

/**
 * 总状态字段：enterStatus
 * 入驻状态说明：
 * 1. comAuditStatus - 企业信息状态
 *    - null: 未进行企业信息填写
 *    - TO_AUDIT: 企业信息审核中
 *    - AUDIT_REJECT: 企业信息审核失败
 *
 * 2. comAuthStatus - 实名认证状态
 *    - null: 未进行实名认证
 *    - AUTHING: 实名认证中
 *    - AUTH_FAIL: 实名认证失败
 *
 * 3. comAuthNodeStatus - 实名认证节点状态
 *    - TRANSFER_AUTHING: 对公转账认证中
 *    - ALIPAY_AUTHING: 支付宝扫码认证中
 *    - TRANSFER_AUTH_FAIL: 对公转账失败
 *    - ALIPAY_AUTH_FAIL: 支付宝扫码认证失败
 *
 * 4. quaAuditStatus - 资质认证状态
 *    - TO_AUDIT: 资质认证审核中
 *    - AUDIT_REJECT: 资质认证审核失败
 */

interface CustomerInfo {
  comAuditStatus?: string
  comAuthStatus?: string
  comAuthNodeStatus?: string
  quaAuditStatus?: string
  enterStatus?: string
}

interface StatusInfo {
  enterStatus: string | null
  tip: string | null
  tipMessage: string | null
  mesColor: [string, string] | null
  route: string | null
  height: number
  justifyontent: 'center' | 'end'
}

const defaultInfo: StatusInfo = {
  enterStatus: '',
  tip: null,
  tipMessage: null,
  mesColor: null,
  route: null,
  height: 0,
  justifyontent: 'center',
}

// 企业信息入驻判断
const getComAuditStatus = (
  enterStatus: string | undefined,
  comAuditStatus?: string,
): StatusInfo | null => {
  if (!comAuditStatus) {
    return {
      ...defaultInfo,
      enterStatus: enterStatus || '',
      tip: '完善企业信息',
      route: `${process.env.YCX_HOME}/go/portal/authenterprise`,
      height: 222,
      justifyontent: 'center',
    }
  }

  if (comAuditStatus === 'TO_AUDIT') {
    return {
      enterStatus: enterStatus || '',
      tip: '查看审核进度 >',
      tipMessage: '企业信息审核中，请耐心等待',
      mesColor: ['rgba(255, 246, 200, 1)', 'rgba(250, 173, 20, 1)'],
      route: `${process.env.YCX_HOME}/go/portal/authstatus`,
      height: 106,
      justifyontent: 'end',
    }
  }

  if (comAuditStatus === 'AUDIT_REJECT') {
    return {
      enterStatus: enterStatus || '',
      tip: '查看审核结果',
      tipMessage: '企业信息审核失败，请查看结果',
      mesColor: ['rgba(255, 221, 215, 1)', 'rgba(255, 77, 79, 1)'],
      route: `${process.env.YCX_HOME}/go/portal/authstatus`,
      height: 106,
      justifyontent: 'end',
    }
  }

  return null
}

// 实名认证入驻判断
const getAuthStatus = (
  enterStatus: string | undefined,
  comAuthStatus?: string,
  comAuthNodeStatus?: string,
): StatusInfo | null => {
  if (!comAuthStatus) {
    return {
      ...defaultInfo,
      enterStatus: enterStatus || '',
      tip: '实名认证',
      route: `${process.env.YCX_HOME}/go/portal/authIdentity`,
      height: 170,
      justifyontent: 'center',
    }
  }

  if (comAuthStatus === 'AUTHING' || comAuthStatus === 'AUTH_FAIL') {
    const route = comAuthNodeStatus?.includes('TRANSFER')
      ? `${process.env.YCX_HOME}/go/portal/authIdentity/authByaccounds`
      : `${process.env.YCX_HOME}/go/portal/authIdentity/authByzfb`

    return {
      enterStatus: enterStatus || '',
      tip: '继续实名认证',
      tipMessage: '实名认证尚未完成，请继续',
      mesColor: ['rgba(255, 246, 200, 1)', 'rgba(250, 173, 20, 1)'],
      route,
      height: 146,
      justifyontent: 'center',
    }
  }

  return null
}

// 资质认证入驻判断
const getQuaAuditStatus = (
  enterStatus: string | undefined,
  quaAuditStatus?: string,
): StatusInfo | null => {
  if (!quaAuditStatus) {
    return {
      ...defaultInfo,
      enterStatus: enterStatus || '',
      tip: '企业资质认证',
      route: `${process.env.QCS_URL}/crm/index_html/bcpcrm/supplierCompanyDetail?returnNew=1&scrollToBottom=true`,
      height: 170,
      justifyontent: 'center',
    }
  }

  if (quaAuditStatus === 'TO_AUDIT') {
    return {
      enterStatus: enterStatus || '',
      tip: '查看审核进度',
      tipMessage: '您的资质审核中，请耐心等待',
      mesColor: ['rgba(255, 246, 200, 1)', 'rgba(250, 173, 20, 1)'],
      route: `${process.env.QCS_URL}/crm/index_html/bcpcrm/supplierCompanyDetail?returnNew=1&scrollToBottom=true`,
      height: 146,
      justifyontent: 'center',
    }
  }

  if (quaAuditStatus === 'AUDIT_REJECT') {
    return {
      enterStatus: enterStatus || '',
      tip: '查看审核结果',
      tipMessage: '您的资质审核失败，请查看审核结果',
      mesColor: ['rgba(255, 221, 215, 1)', 'rgba(255, 77, 79, 1)'],
      route: `${process.env.QCS_URL}/crm/index_html/bcpcrm/supplierCompanyDetail?returnNew=1&scrollToBottom=true`,
      height: 146,
      justifyontent: 'center',
    }
  }

  return null
}

export const useStatusInfo = (
 
) => {
  const getStatusInfo = useCallback(( customerInfo: CustomerInfo | null,
    role: string,) => {
    if (!customerInfo && role === 'buyer') {
      return {
        ...defaultInfo,
        enterStatus: 'ENTER_PASS',
        tip: '大企采工作台',
        route: `${process.env.YCX_HOME}/become-buyer`,
        mesColor: ['', ''],
        height: 130,
        justifyontent: 'end',
      }
    }
    if (
      customerInfo?.comAuditStatus === 'AUDIT_PASS' &&
      role === 'free_buyer'
    ) {
      return {
        ...defaultInfo,
        enterStatus: 'ENTER_PASS',
        tip: '了解成为采购商',
        route: `${process.env.YCX_HOME}/become-buyer`,
        mesColor: ['', ''],
        height: 197,
        justifyontent: 'center',
      }
    }
    if (!customerInfo) {
      return {
        ...defaultInfo,
        tip: '完善企业信息',
        route: ` ${process.env.YCX_HOME}/go/portal/authenterprise`,
        height: 222,
        justifyontent: 'center',
      }
    }

    const { enterStatus } = customerInfo

    if (enterStatus === 'ENTER_PASS') {
      return {
        ...defaultInfo,
        enterStatus,
        height: 130,
        justifyontent: 'end',
      }
    }

    // 非供应商角色只判断企业信息状态
    if (role !== 'supplier') {
      const comAuditResult = getComAuditStatus(
        enterStatus,
        customerInfo.comAuditStatus,
      )
      return (
        comAuditResult || {
          ...defaultInfo,
          enterStatus:
            customerInfo.comAuditStatus === 'AUDIT_PASS' ? 'ENTER_PASS' : '',
          tip: role === 'buyer' ? '大企采工作台' : '了解成为采购商',
          route: `${process.env.YCX_HOME}/become-buyer`,
          mesColor: ['', ''],
          height: 130,
          justifyontent: 'end',
        }
      )
    }

    // 供应商角色判断所有状态
    const comAuditResult = getComAuditStatus(
      enterStatus,
      customerInfo.comAuditStatus,
    )
    if (comAuditResult) return comAuditResult

    const authResult = getAuthStatus(
      enterStatus,
      customerInfo.comAuthStatus,
      customerInfo.comAuthNodeStatus,
    )
    if (authResult) return authResult

    const quaAuditResult = getQuaAuditStatus(
      enterStatus,
      customerInfo.quaAuditStatus,
    )
    if (quaAuditResult) return quaAuditResult
    if (
      customerInfo?.comAuditStatus === 'AUDIT_PASS' &&
      customerInfo?.comAuthStatus === 'AUTH_PASS' &&
      customerInfo?.quaAuditStatus === 'AUDIT_PASS'
    ) {
      return {
        ...defaultInfo,
        enterStatus: 'ENTER_PASS',
        tip: '大企采工作台',
        route: `${process.env.YCX_HOME}/become-buyer`,
        mesColor: ['', ''],
        height: 130,
        justifyontent: 'end',
      }
    }
    return defaultInfo
  }, [])

  return { getStatusInfo  }
}
